// components/projects/FileUpload.js - OPRAVENÁ VERZE s kompresí obrázků
import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Image as ImageIcon, File, FilePlus, ChevronDown, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getFileTypeInfo } from '../../utils/fileUtils';
import { compressImage } from '../../utils/imageUtils'; // ✅ PŘIDÁNO: Import komprese
import { fileTags } from '../../services/api';
import { getEnabledFileTags, getLocalizedLabel } from '../../utils/fileTagsUtils';

const FileUpload = ({ isOpen, onClose, onSave }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [description, setDescription] = useState('');
  const [selectedTag, setSelectedTag] = useState('document');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  
  // NOVÝ STAV pro drag and drop
  const [isDragOver, setIsDragOver] = useState(false);
  
  const dropdownRef = useRef(null);
  const dragCounter = useRef(0); // Pro sledování drag událostí

  // Max file size (10MB)
  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  // Reset function
  const resetState = () => {
    setFiles([]);
    setDescription('');
    setSelectedTag('document');
    setError('');
    setUploadProgress({});
    setIsDropdownOpen(false);
    setIsDragOver(false); // Reset drag state
    dragCounter.current = 0; // Reset drag counter
  };

  // Reset při otevření modalu
  useEffect(() => {
    if (isOpen) {
      resetState();
      loadSettings();
    }
  }, [isOpen]);

  // ✅ Listen for file tags updates
  useEffect(() => {
    const handleFileTagsUpdate = () => {
      console.log('📢 File tags updated in FileUpload, refreshing...');
      loadSettings();
    };

    window.addEventListener('fileTagsUpdated', handleFileTagsUpdate);

    return () => {
      window.removeEventListener('fileTagsUpdated', handleFileTagsUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoadingSettings(true);
      const response = await fileTags.getAll();
      setSettings({ fileTags: response.data });
    } catch (error) {
      console.warn('Could not load file tags, using defaults');
      setSettings({ fileTags: [] });
    } finally {
      setIsLoadingSettings(false);
    }
  };

  // Get available file tags
  const getFileTags = () => {
    if (settings && settings.fileTags) {
      const tags = getEnabledFileTags(settings).filter(tag => tag.code !== 'all');
      return tags.length > 0 ? tags : getDefaultTags();
    }
    return getDefaultTags();
  };

  const getDefaultTags = () => [
    { code: 'document', labels: { en: 'Documents', cs: 'Dokumenty' }, enabled: true },
    { code: 'contract', labels: { en: 'Contracts', cs: 'Smlouvy' }, enabled: true },
    { code: 'technical', labels: { en: 'Technical', cs: 'Technické' }, enabled: true },
    { code: 'inspection', labels: { en: 'Inspection', cs: 'Inspekce' }, enabled: true },
    { code: 'implementation', labels: { en: 'Implementation', cs: 'Realizace' }, enabled: true },
    { code: 'invoice', labels: { en: 'Invoices', cs: 'Faktury' }, enabled: true }
  ];

  // Validate file before adding
  const validateFile = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(t('files.errors.fileTooLarge', `File "${file.name}" is too large. Maximum size is 10MB.`));
    }

    // Check file type
    const allowedTypes = [
      'image/', 'application/pdf', 'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/'
    ];

    const isAllowedType = allowedTypes.some(type => file.type.startsWith(type));
    if (!isAllowedType) {
      throw new Error(t('files.errors.invalidType', `File type "${file.type}" is not supported.`));
    }

    return true;
  };

  // ✅ OPRAVENÁ FUNKCE: Process files s kompresí obrázků
  const processFiles = async (fileList) => {
    const selectedFiles = Array.from(fileList);
    setError('');

    if (!selectedFiles.length) return;

    try {
      const validFiles = [];
      
      for (const file of selectedFiles) {
        validateFile(file);
        
        // ✅ NOVÉ: Komprimuj obrázky už při přidávání do seznamu
        let processedFile = file;
        let wasCompressed = false;
        
        if (file.type.startsWith('image/')) {
          try {
            console.log(`🗜️ Compressing image: ${file.name} (${(file.size / 1024).toFixed(1)}KB)`);
            const compressed = await compressImage(file);
            
            // ✅ OPRAVA: Použij Blob místo File (kompatibilnější)
            const base64Data = compressed.base64.split(',')[1];
            const byteArray = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
            
            // Vytvoř Blob a pak File
            const blob = new Blob([byteArray], { type: 'image/jpeg' });
            
            // Bezpečné vytvoření File objektu
            try {
              processedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: file.lastModified || Date.now()
              });
            } catch (fileError) {
              // Fallback: pokud File konstruktor nefunguje, použij Blob s přidanými vlastnostmi
              processedFile = blob;
              processedFile.name = file.name;
              processedFile.lastModified = file.lastModified || Date.now();
            }
            
            wasCompressed = true;
            console.log(`✅ Compression complete: ${(file.size / 1024).toFixed(1)}KB → ${(processedFile.size / 1024).toFixed(1)}KB (${((1 - processedFile.size / file.size) * 100).toFixed(1)}% saved)`);
            
          } catch (compressionError) {
            console.warn('⚠️ Compression failed, using original:', compressionError);
            // Při chybě komprese použij originální soubor
          }
        }
        
        // Create file object with metadata
        const fileObj = {
          id: Date.now() + Math.random(),
          file: processedFile, // ✅ Použij komprimovaný soubor
          name: file.name, // Zachovej původní název
          type: processedFile.type,
          size: processedFile.size,
          originalSize: file.size, // Pro zobrazení úspory
          wasCompressed,
          preview: null
        };

        // Generate preview for images
        if (file.type.startsWith('image/')) {
          try {
            fileObj.preview = await createImagePreview(processedFile);
          } catch (err) {
            console.warn('Failed to create image preview:', err);
          }
        }

        validFiles.push(fileObj);
      }

      setFiles(prevFiles => [...prevFiles, ...validFiles]);
      
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle file selection (pro input element)
  const handleFileChange = async (e) => {
    await processFiles(e.target.files);
    // Reset input
    e.target.value = '';
  };

  // ✅ OPRAVENÉ DRAG AND DROP HANDLERY - jednodušší a spolehlivější
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    console.log('🎯 Drag enter, counter:', dragCounter.current);
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    console.log('🎯 Drag leave, counter:', dragCounter.current);
    
    // Pouze pokud skutečně opouštíme celou oblast
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('🎯 Drag over');
    
    // Vždy nastavíme drag over při pohybu
    if (!isDragOver) {
      setIsDragOver(true);
    }
    
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('🎯 Drop event');
    
    dragCounter.current = 0;
    setIsDragOver(false);

    if (isLoading) return;

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      console.log('🗂️ Files dropped:', droppedFiles.length);
      await processFiles(droppedFiles);
    }
  };

  // Create image preview (compressed)
  const createImagePreview = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate scaled dimensions
          const maxSize = 200;
          let { width, height } = img;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Remove file from list
  const handleRemoveFile = (fileId) => {
    setFiles(prevFiles => prevFiles.filter(f => f.id !== fileId));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
  };

  // Handle close
  const handleClose = () => {
    if (!isLoading) {
      resetState();
      onClose();
    }
  };

  // ✅ OPRAVENÁ FUNKCE: Handle form submission s vícenásobným uploadem
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!files.length) {
      setError(t('files.errors.noFiles', 'Please select at least one file'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      console.log('📤 Uploading', files.length, 'files with tag:', selectedTag);
      
      // ✅ OPRAVA: Upload souborů jeden po druhém
      for (let i = 0; i < files.length; i++) {
        const fileObj = files[i];
        
        // Aktualizuj progress pro aktuální soubor
        setUploadProgress(prev => ({
          ...prev,
          [fileObj.id]: { progress: 0, status: 'uploading' }
        }));
        
        try {
          // ✅ Soubory už jsou komprimované z processFiles, jen je přidáme do FormData
          const formData = new FormData();
          formData.append('file', fileObj.file); // ✅ Už je komprimovaný
          formData.append('tags', selectedTag);
          formData.append('description', description);
          
          console.log(`📤 Uploading file ${i + 1}/${files.length}:`, fileObj.name, 
                      fileObj.wasCompressed ? '(compressed)' : '(original)');
          
          // Upload individual file s progress tracking
          await onSave(formData, (progress) => {
            console.log(`📊 File ${i + 1} progress: ${progress}%`);
            setUploadProgress(prev => ({
              ...prev,
              [fileObj.id]: { progress, status: 'uploading' }
            }));
          });
          
          // Označ soubor jako dokončený
          setUploadProgress(prev => ({
            ...prev,
            [fileObj.id]: { progress: 100, status: 'completed' }
          }));
          
          console.log(`✅ File ${i + 1} uploaded successfully:`, fileObj.name);
          
        } catch (fileError) {
          console.error(`❌ File ${i + 1} upload failed:`, fileError);
          setUploadProgress(prev => ({
            ...prev,
            [fileObj.id]: { progress: 0, status: 'error' }
          }));
          
          // Pokračuj s dalšími soubory i při chybě
          setError(`Upload failed for ${fileObj.name}: ${fileError.message}`);
        }
      }
      
      console.log('✅ All files processed');
      
      // ✅ Zavři modal až po dokončení všech uploadů
      setTimeout(() => {
        resetState();
        onClose();
      }, 1000);
      
    } catch (error) {
      console.error('❌ Upload error:', error);
      setError(error.message || 'Upload failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Render file preview
  const renderFilePreview = (fileObj) => {
    const { icon, color } = getFileTypeInfo(fileObj.type);
    const progress = uploadProgress[fileObj.id];

    return (
      <div key={fileObj.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-md">
        <div className="flex-shrink-0">
          {fileObj.preview ? (
            <img 
              src={fileObj.preview} 
              alt={fileObj.name}
              className="h-12 w-12 object-cover rounded"
            />
          ) : (
            <div className={`h-12 w-12 rounded flex items-center justify-center bg-${color}-500`}>
              {React.createElement(icon, { className: "h-6 w-6 text-white" })}
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {fileObj.name}
          </p>
          <p className="text-xs text-gray-500">
            {(fileObj.size / 1024 / 1024).toFixed(2)} MB
            {fileObj.wasCompressed && (
              <span className="text-green-600 ml-1">
                (saved {((1 - fileObj.size / fileObj.originalSize) * 100).toFixed(0)}%)
              </span>
            )}
          </p>
          
          {progress && (
            <div className="mt-1">
              <div className="bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all ${
                    progress.status === 'completed' ? 'bg-green-500' :
                    progress.status === 'error' ? 'bg-red-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${progress.progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {progress.status === 'completed' ? 'Completed' :
                 progress.status === 'error' ? 'Error' :
                 `${progress.progress}%`}
              </p>
            </div>
          )}
        </div>
        
        <button
          type="button"
          onClick={() => handleRemoveFile(fileObj.id)}
          disabled={isLoading}
          className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  };

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
            {t('files.title', 'Upload Files')}
          </h3>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* File Category Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('files.category', 'File Category')}
            </label>
            <div className="relative mt-1" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                disabled={isLoading || isLoadingSettings}
                className="relative w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
              >
                <span className="block truncate">
                  {isLoadingSettings ? (
                    t('common.loading', 'Loading...')
                  ) : (
                    getLocalizedLabel(getFileTags().find(tag => tag.code === selectedTag))
                  )}
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                </span>
              </button>
              
              {isDropdownOpen && !isLoadingSettings && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
                  {getFileTags().map((tag) => (
                    <div
                      key={tag.code}
                      onClick={() => {
                        setSelectedTag(tag.code);
                        setIsDropdownOpen(false);
                      }}
                      className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50 ${
                        selectedTag === tag.code ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                      }`}
                    >
                      <span className="block truncate">{getLocalizedLabel(tag)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* File Upload Area */}
          <div>
            <div
              className={`relative border-2 border-dashed rounded-lg p-6 transition-all duration-300 ${
                isDragOver 
                  ? 'border-blue-400 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                disabled={isLoading}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className={`mx-auto h-12 w-12 transition-colors duration-300 ${
                  isDragOver ? 'text-blue-600 animate-pulse' : 'text-gray-400'
                }`} />
                <div className="mt-2">
                  <span className={`font-medium transition-colors duration-300 ${
                    isDragOver ? 'text-blue-700' : 'text-blue-600'
                  }`}>
                    {isDragOver 
                      ? t('files.dropFiles', 'Přetáhněte soubory sem')
                      : t('files.clickToUpload', 'Klikněte pro nahrání souborů')
                    }
                  </span>
                  {!isDragOver && (
                    <span className="text-gray-500"> {t('files.orDragDrop', 'nebo přetáhněte')}</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('files.supportedFormats', 'Obrázky, PDF, dokumenty Office do 10MB')}
                </p>
                
                {/* DEBUG INFO - odstraňte po otestování */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="mt-2 text-xs text-gray-400">
                    Debug: isDragOver = {isDragOver.toString()}
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Selected Files List */}
          {files.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('files.selectedFiles', 'Selected Files')} ({files.length})
              </label>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {files.map(renderFilePreview)}
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('files.description', 'Description')}
            </label>
            <textarea
              rows="3"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('files.descriptionPlaceholder', 'Add a description for the files...')}
              disabled={isLoading}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              {t('common.actions.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading || !files.length}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {isLoading && <Loader className="h-4 w-4 animate-spin" />}
              <span>
                {isLoading 
                  ? t('files.uploading', 'Uploading...')
                  : t('files.uploadButton', {
                      count: files.length,
                      defaultValue: `Upload ${files.length} ${files.length === 1 ? 'File' : 'Files'}`
                    })
                }
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FileUpload;